/**
 * PDP zoom lightbox. Replaces Dawn's stacked-scroll product-modal.
 * Native <dialog>, one image at a time, prev/next, Escape / backdrop close.
 * `show(opener)` stays the contract modal-opener.js and product-info.js use.
 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends HTMLElement {
      /** @type {HTMLDialogElement | null} */
      dialog = null;
      /** @type {HTMLElement | null} */
      openedBy = null;
      /** @type {AbortController | null} */
      #controller = null;
      /** @type {number} */
      #index = 0;

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.#controller = new AbortController();
        const { signal } = this.#controller;

        this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.hide(), { signal });
        this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
        this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.#step(1), { signal });

        this.dialog.addEventListener(
          'click',
          (event) => {
            if (event.target === this.dialog) this.hide();
          },
          { signal }
        );

        this.dialog.addEventListener(
          'close',
          () => {
            this.removeAttribute('open');
            if (this.openedBy && typeof this.openedBy.focus === 'function') {
              this.openedBy.focus();
            }
          },
          { signal }
        );

        this.dialog.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.#step(-1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.#step(1);
            }
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.#controller?.abort();
      }

      /**
       * @param {HTMLElement} opener
       */
      show(opener) {
        this.openedBy = opener;
        const mediaId = opener?.getAttribute('data-media-id');
        const slides = this.#slides();
        let index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
        if (index < 0) index = 0;
        this.#showIndex(index);

        this.setAttribute('open', '');
        if (typeof this.dialog?.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog?.setAttribute('open', '');
        }

        this.querySelector('[data-zoom-close]')?.focus();
      }

      hide() {
        if (typeof this.dialog?.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog?.removeAttribute('open');
          this.removeAttribute('open');
        }
      }

      /** @returns {HTMLElement[]} */
      #slides() {
        return Array.from(this.querySelectorAll('[data-zoom-slide]'));
      }

      /**
       * @param {number} delta
       */
      #step(delta) {
        const slides = this.#slides();
        if (slides.length < 2) return;
        const next = (this.#index + delta + slides.length) % slides.length;
        this.#showIndex(next);
      }

      /**
       * @param {number} index
       */
      #showIndex(index) {
        const slides = this.#slides();
        if (!slides.length) return;
        this.#index = index;

        for (let i = 0; i < slides.length; i += 1) {
          const active = i === index;
          slides[i].hidden = !active;
          slides[i].classList.toggle('is-active', active);
        }

        const status = this.querySelector('[data-zoom-status]');
        if (status) {
          status.textContent = String(index + 1);
        }

        const prev = this.querySelector('[data-zoom-prev]');
        const next = this.querySelector('[data-zoom-next]');
        const many = slides.length > 1;
        if (prev) prev.hidden = !many;
        if (next) next.hidden = !many;
      }
    }
  );
}
